import logger from '../../utils/logger';
import { toNumber } from '../../utils/numbers';
import convertUnit from '../../utils/unit-converter';
import { ScaleTypes } from '../discharge-store';
import {
  calculateWaterTotals,
  applyDropAdjustments,
} from '../../containers/Batch/utils/water-utils';
import { isFeatureEnabled } from '../../utils/feature-flags';

const FEATURE_ALLOW_OVERCAPACITY_IN_ONE_DROP = isFeatureEnabled('allow_overcapacity_in_one_drop');
const FEATURE_ALLOW_BATCH_WITH_INDICATOR_ERROR = isFeatureEnabled('allow_batch_with_indicator_error');

/**
 * Ensures water doesn't exceed scale capacity, even after
 * auto drop adjustments (wash water gets automatically
 * readjusted if head water exceeds the scale cap)
 */
const checkWaterScaleCapacity = ({ data, batchStore, materialStore, devicesStore }) => {
  const errors = [];

  if (!data?.mixDesign || data.allDrops <= 0) {
    return errors;
  }

  const sequence = data.sequence || batchStore.getSequence(data);
  const waterScale = sequence?.discharges?.find(s => s.type === ScaleTypes.WATER);

  if (!waterScale) {
    return errors;
  }

  // Skip water without a device is never fed, so it never goes on the water scale.
  const waters = data.mixDesign.filter(v =>
    (materialStore.isWater(v.constituentItemId) || materialStore.isSecondWater(v.constituentItemId))
    && !materialStore.isSkipMaterialWithoutDevice(v)
  );

  if (waters.length === 0) {
    return errors;
  }

  const waterScaleDevice = devicesStore.getDeviceById(waterScale.scaleId);
  const waterScaleUnit = devicesStore.getUnitSymbolByUnitId(waterScaleDevice?.unitId);
  const waterScaleCapacity = waterScaleDevice?.capacity || 0;
  const waterUnit = materialStore.getConstituentUnitSymbol(waters[0]);

  const totals = calculateWaterTotals({
    waters,
    dropSize: data.dropSize || 1,
    allDrops: data.allDrops,
    sequence,
    waterUnit,
    outputUnit: waterScaleUnit,
  });

  const firstDrop = applyDropAdjustments({
    headWater: totals.headWaterPerDrop,
    washWater: totals.washWaterPerDrop,
    sequence,
    currentDrop: 1,
    allDrops: data.allDrops,
    waterScaleCapacity,
    totalHeadWaterForAllWaters: totals.totalHeadWater,
  });

  if (firstDrop.headWater > waterScaleCapacity) {
    if (allowOvercapacityInOneDrop(waterScaleDevice, waterScale.type, firstDrop.headWater, data)) {
      logger.log('allow overcapacity head water', waterScale.type, firstDrop.headWater);
    } else {
      errors.push({
        name: 'waterScaleCapacityFirstDrop',
        error: `Head water ${toNumber(firstDrop.headWater, 1)} ${waterScaleUnit} on first drop exceeds water scale capacity ${waterScaleCapacity} ${waterScaleUnit}!`,
      });
    }
  }

  const lastDrop = applyDropAdjustments({
    headWater: totals.headWaterPerDrop,
    washWater: totals.washWaterPerDrop,
    sequence,
    currentDrop: data.allDrops,
    allDrops: data.allDrops,
    waterScaleCapacity,
    totalHeadWaterForAllWaters: totals.totalHeadWater,
  });

  if (lastDrop.washWater > waterScaleCapacity) {
    if (allowOvercapacityInOneDrop(waterScaleDevice, waterScale.type, lastDrop.washWater, data)) {
      logger.log('allow overcapacity wash water', waterScale.type, lastDrop.washWater);
    } else {
      errors.push({
        name: 'waterScaleCapacityLastDrop',
        error: `Wash water ${toNumber(lastDrop.washWater, 1)} ${waterScaleUnit} on last drop exceeds water scale capacity ${waterScaleCapacity} ${waterScaleUnit}!`,
      });
    }
  }

  // When there's only one drop with both flags enabled, both head and wash water
  // are batched on the same drop - need to check their combined total
  if (
    data.allDrops === 1 &&
    sequence.allWaterOnFirstDrop &&
    sequence.allWashWaterOnLastDrop
  ) {
    const combinedWater = firstDrop.headWater + firstDrop.washWater;

    if (combinedWater > waterScaleCapacity) {
      if (allowOvercapacityInOneDrop(waterScaleDevice, waterScale.type, combinedWater, data)) {
        logger.log('allow overcapacity all water', waterScale.type, combinedWater);
      } else {
        errors.push({
          name: 'waterScaleCapacitySingleDrop',
          error: `Total water ${toNumber(combinedWater, 1)} ${waterScaleUnit} on single drop exceeds water scale capacity ${waterScaleCapacity} ${waterScaleUnit}!`,
        });
      }
    }
  }

  return errors;
};

export const isLiveMoistureInMix = (data) => {
  if (!data.scales) {
    return false;
  }

  let isLiveMoisture = false;

  Object.keys(data.scales).forEach(scaleId => {
    data.scales[scaleId].forEach((dataForScale) => {
      if (dataForScale.materialDetails.liveMoisture) {
        isLiveMoisture = true;
      }
    });
  });

  return isLiveMoisture;
};

const allowOvercapacityInOneDrop = (scale, scaleType, target, data) =>  {
  return scaleType === ScaleTypes.WATER
    && !isLiveMoistureInMix(data)
    && FEATURE_ALLOW_OVERCAPACITY_IN_ONE_DROP
    && scale.allowOvercapacityInOneDrop
    && data.allDrops === 1
    && target <= scale.capacity * 1.5;//allow only +50% over scale capacity
};

export const canBatch = (data, rootStore) => {
  const { batchStore, settingsStore, materialStore, scheduleStore, devicesStore, dischargeStore } = rootStore;
  const { configuration } = settingsStore;
  const { maximumBatchSize = 0, maximumDropSize = 0 } = configuration;

  logger.log('canBatch: ', data);
  let errorsToOverride = [];
  let errors = [];
  let warnings = [];

  if (batchStore.maintenanceMode) {
    errors.push({ name: 'maintenanceMode', error: 'Maintenance Mode is ON!' });
  }

  if (batchStore.queueSize >= batchStore.maxQueueSize) {
    errors.push({ name: 'maxQueueSize', error: `Maximum Queue Size: ${batchStore.maxQueueSize}!` });
  }

  if (data.dropSize > maximumDropSize) {
    errors.push({ name: 'maxDropSize', error: `Maximum Drop Size: ${maximumDropSize}!` });
  }

  if (data.batchSize > maximumBatchSize) {
    errors.push({ name: 'maxBatchSize', error: `Maximum Batch Size: ${maximumBatchSize}!` });
  }

  if (!batchStore.isDemo && batchStore.indicatorError) {
    errors.push({ name: 'indicatorError', error: 'Scale indicator error!' });
  }

  if (!batchStore.isDemo && !batchStore.pacConnected && !batchStore.canSendRemoteInstruction()) {
    errors.push({ name: 'pacConnected', error: 'Plant disconnected!' });
  }

  if (!batchStore.context.canStartLoad(batchStore.inputs, batchStore.outputs, batchStore.channels)) {
    errors.push({ name: 'logic', error: 'Can\'t start the load due to condition in the Logic Editor!' });
  }

  data = batchStore.getDataForPumpsAndScales(data);

  if (data?.error) {
    logger.warn(data.error);
    errors.push({ name: 'data', error: data.error });
  }

  if (!data || (!data.scales && !data.pumps)) {
    logger.warn(`No pump nor scale assigned!
      or Quantity in Truck must be lower or equal to Quantity to Batch`, data);
    if (!data || (Number(data.quantityToBeBatched) < Number(data.mixQuantityInTruck))) {
      errors.push({ name: 'noDevice', error: 'Quantity in Truck must be lower or equal to Quantity to Batch' });
    }
  }

  if (data.truckId) {
    const truck = scheduleStore.trucks.find(v => v.id === Number(data.truckId));
    if (truck && Number(data.quantityToBeBatched || 0) > Number(truck.maximumLoadSize || 0)) {
      logger.log('truck capacity check:', Number(data.quantityToBeBatched || 0), '>', Number(truck.maximumLoadSize || 0));
      warnings.push('Truck capacity exceeded!');
    }
  }

  if (data.scales) {
    Object.keys(data.scales).forEach(scaleId => {
      let target = 0;
      let scale = {};
      let md = {};
      let unit = '';
      const scaleType = dischargeStore.getTypeForScale(scaleId);

      data.scales[scaleId].forEach(v => {
        target += v.mft;
        scale = v.scale;
        md = v.materialDetails;
        const name = materialStore.getMaterialName(v.material);
        unit = devicesStore.getUnitSymbolByUnitId(scale.unitId);
        let currentBalance = toNumber(convertUnit(v.material.currentBalance, v.material.itemInventoryUnitSymbol, unit), 2);
        const weight = batchStore.getScaleWeight(scale);

        if (scale.decumulative) {
          if (weight < v.mft && !data.overrideErrors) {
            errorsToOverride.push({ name: 'materialInDecum', error: `Not enough material ${name} in ${scale.description}, ${weight} < ${v.mft}!` });
          }
        }

        if (
          batchStore.isIndicatorValueError(weight)
          && !data.overrideErrors
          && !errorsToOverride.some(e => e.name === 'indicatorValueError' && scale.id === e.scaleId)
        ) {
          const error = {
            name: 'indicatorValueError',
            error: `Scale indicator value error! ${scale.description}`,
            scaleId: scale.id,
          };

          if (
            FEATURE_ALLOW_BATCH_WITH_INDICATOR_ERROR
            && configuration.allowBatchWithScaleIndicatorError
          ) {
            errorsToOverride.push(error);
          } else {
            errors.push(error);
          }
        }

        if (currentBalance < v.mft) {
          warnings.push(`Not enough material ${name},
             current balance: ${currentBalance} ${unit}, target: ${toNumber(v.mft, 2)} ${unit}!`);
        }

        if (v.mft > md.maximumAmountInMix && md.maximumAmountInMix > 0 && !data.materialId) {
          warnings.push(`${name}: Maximum amount for ingredient ${md.maximumAmountInMix} ${md.itemBatchUnitSymbol}!`);
        }

        if (v.mft > md.maximumDropLimit && md.maximumDropLimit > 0 && !data.materialId) {
          errors.push({
            name: 'maximumDropLimit',
            error: `${name}: Maximum drop limit ${md.maximumDropLimit} ${unit}!`,
            target: v.mft * data.allDrops,
            limit: md.maximumDropLimit,
          });
        }
      });

      target = toNumber(target, 0);

      if (target > scale.capacity) {
        if (allowOvercapacityInOneDrop(scale, scaleType, target, data)) {
          logger.log('allow water scale over target', scale.description, scaleType, target, scale.capacity);
        } else {
          errors.push({
            name: 'scaleCapacity',
            error: `Target ${target} ${unit} for scale ${scale.description} is over capacity ${scale.capacity} ${unit}!`,
            target: target * data.allDrops,
            capacity: scale.capacity,
          });
        }
      }

      if (target > scale.maximumLoad) {
        errors.push({ name: 'maximumLoad', error: `Target ${target} ${unit} for scale ${scale.description} is over maximum load ${scale.maximumLoad} ${unit}!` });
      }
    });
  }

  errors.push(...checkWaterScaleCapacity({ data, batchStore, materialStore, devicesStore }));

  if (data?.material) {
    if (!configuration.enableToggleMaterialVisibility && !data.overrideErrors) {
      if (!data.material.visible) {
        errors.push({ name: 'materialVisible', error: `Material ${materialStore.getMaterialName(data.material)} is not visible!` });
      }
      if (materialStore.isOtherMaterialWithTheSameOutputVisible(data.material)) {
        errors.push({ name: 'materialSameOutput', error: 'More than one material share the same bin output!' });
      }
    }

    const bins = devicesStore.getActiveBinsForMultiScale(data.material, true);

    const getTotalBinPercent = (binPercent) => {
      let percent = 0;
      Object.values(binPercent).forEach(v => {
        percent += Number(v) || 0;
      });

      return percent;
    };

    if (bins?.length && !getTotalBinPercent(data.binPercent)) {
      errors.push({ name: 'binPercent', error: 'Quantity per bin is not set!' });
    }
  }

  if (data?.mixDesign) {
    data.mixDesign.forEach(constituent => {
      const material = constituent.batchCode ? materialStore.getMaterialByBatchCode(constituent.batchCode) : materialStore.getMaterialByItemId(constituent.constituentItemId);
      const materialDetails = material ? materialStore.materialDetails[material.materialDetailId] : null;
      const name = materialStore.getConstituentName(constituent);
      const isDeviceSet = materialStore.isDeviceSetToConstituent(constituent)
        || materialStore.isSkipMaterialWithoutDevice(constituent);

      if (!material || !materialDetails?.type || !isDeviceSet) {
        const message = `Missing material ${name}`;

        if (materialDetails?.disallowBatchIfMissing) {
          errors.push({ name: 'missingMaterial', error: message });
        } else if (configuration.disallowBatchIfConstituentIsMissing) {
          errors.push({ name: 'missingMaterial', error: message });
        } else {
          warnings.push(message);
        }
      }

      if (!configuration.enableToggleMaterialVisibility && constituent.quantityNet && !data.overrideErrors) {
        if (material && !material.visible) {
          errorsToOverride.push({ name: 'notVisible', error: `Material ${name} is not visible!` });
        }

        if (material && materialStore.isOtherMaterialWithTheSameOutputVisible(material)) {
          errorsToOverride.push({ name: 'sameOutput', error: 'More than one material share the same bin output!' });
        }
      }

    });

    const md = data.mixDesign.map(v => v.constituentItemId);
    const unique = Array.from(new Set(md));

    if (unique.length !== md.length) {
      warnings.push('Mix Design contains duplicate constituent!');
    }

    // Check if 2 separate materials in the mix design share the same active bin
    const binMaterialMap = new Map(); // Map of binId -> array of material names
    data.mixDesign.forEach(constituent => {
      const material = constituent.batchCode
        ? materialStore.getMaterialByBatchCode(constituent.batchCode)
        : materialStore.getMaterialByItemId(constituent.constituentItemId);

      if (material && material.bins) {
        const activeBins = material.bins.filter(bin => bin.isActive);
        const materialName = materialStore.getConstituentName(constituent);

        activeBins.forEach(bin => {
          if (!binMaterialMap.has(bin.binId)) {
            binMaterialMap.set(bin.binId, []);
          }
          binMaterialMap.get(bin.binId).push(materialName);
        });
      }
    });

    // Check for duplicate bin assignments
    binMaterialMap.forEach((materials, binId) => {
      if (materials.length > 1) {
        const materialsList = materials.join(' and ');
        errors.push({
          name: 'duplicateBinInMix',
          error: `Materials ${materialsList} share the same active bin (Bin #${binId}). Please deactivate one of the bins before batching.`,
        });
      }
    });

  }

  if (data.pumps) {
    Object.keys(data.pumps).forEach(pumpId => {
      const pumpData = data.pumps[pumpId];
      const md = pumpData.materialDetails;
      let unit = md.itemBatchUnitSymbol;
      let currentBalance = toNumber(convertUnit(pumpData.material.currentBalance, pumpData.material.itemInventoryUnitSymbol, unit), 2);
      const name = materialStore.getMaterialName(pumpData.material);

      if (currentBalance < pumpData.mft) {
        warnings.push(`Not enough material ${name},
             current balance: ${currentBalance} ${unit}, target: ${toNumber(pumpData.mft, 2)} ${unit}!`);
      }

      if (pumpData.mft > md.maximumAmountInMix && md.maximumAmountInMix > 0 && !data.materialId) {
        warnings.push(`${name}: Maximum amount for ingredient ${md.maximumAmountInMix} ${unit}! `);
      }

      if (pumpData.mft > md.maximumAmountInLoad && md.maximumAmountInLoad > 0 && !data.materialId) {
        errors.push({
          name: 'maximumAmountInLoad',
          error: `${name}: Maximum amount in load ${md.maximumAmountInLoad} ${unit}!`,
          target: pumpData.mft * data.allDrops,
          limit: md.maximumAmountInLoad,
        });
      }
    });
  }

  const response = {
    isValid: !errors.length && !errorsToOverride.length,
    errorsToOverride,
    errors,
    warnings,
    mixDifferWarnings: batchStore.checkIfMixIsDifferFromMixInTruck(data),
  };

  logger.log('can batch', response);
  return response;
};
